import {
	type ActionItem,
	type Call as BaseCall,
	CALL_STATUS,
	type CallStatus,
	type CollectedNumber,
	isCallStatus,
} from "@repo/common";
import { type DocumentData } from "firebase/firestore";

export interface Call
	extends Partial<
		Omit<BaseCall, "evaluationStatus" | "callType" | "from" | "duration">
	> {
	id: string;
	createdAt: string;
	caller: {
		name: string;
		phone: string;
		email?: string;
	};
	agent: string;
	inboundNumber?: string;
	occurredAt: Date;
	date: string; // YYYY-MM-DD
	time: string; // HH:MM AM/PM
	duration: string; // MM:SS or Xs
	status: CallStatus;
	isTestCall?: boolean;
	summary?: string;
	transcript?: string;
	collectedNumbers?: CollectedNumber[];
	title?: string;
	gcsUri?: string;
	companyId: string; // Useful for admin to know which company
	actionItems: ActionItem[];
	evaluationScore?: number;
	evaluationStatus?: "pending" | "completed" | "error";
	isProblematic?: boolean;
	latencyMs?: number;
	evaluationUsage?: {
		inputTokens?: number;
		outputTokens?: number;
		totalTokens?: number;
	};
	evaluationCost?: number;
	source?: string | null;
	outboundPlaybookRunId?: string | null;
	outboundTaskId?: string | null;
	outboundTaskAttemptId?: string | null;
	outboundPlaybookId?: string | null;
	outboundPlaybookVersion?: number | null;
	destinationNumber?: string | null;
	from?: string | null;
	operatorPhoneNumber?: string | null;
	operatorName?: string | null;
	callType?: "ai" | "human" | null;
}

export interface EvaluationReport {
	score: number;
	agentPerformance: {
		handlingScore: number;
		adherenceToDirectivesScore: number;
		feedback: string;
	};
	postProcessingQuality: {
		summaryScore: number;
		tagsScore: number;
		nameScore: number;
		feedback: string;
	};
	isProblematic: boolean;
	problematicReason: string;
	createdAt?: Date | { toDate?: () => Date } | string | null;
}

export function toCallUi(
	id: string,
	data: DocumentData,
	companyId: string,
): Call {
	const aiCallerName =
		typeof data.callerName === "string" && data.callerName.trim()
			? data.callerName.trim()
			: null;
	const occurredAtDate =
		data.occurredAt?.toDate() ||
		(data.dateCreated ? new Date(data.dateCreated) : null) ||
		data.createdAt?.toDate() ||
		new Date();
	const date = occurredAtDate.toISOString().split("T")[0];

	const createdAtDate = data.createdAt?.toDate() || occurredAtDate;
	const latencyMs = createdAtDate.getTime() - occurredAtDate.getTime();

	let hours = occurredAtDate.getHours();
	const minutes = occurredAtDate.getMinutes();
	const ampm = hours >= 12 ? "PM" : "AM";
	hours = hours % 12;
	hours = hours ? hours : 12;
	const time = `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;

	let durationStr = "0s";
	const seconds = Number(data.duration);
	if (!isNaN(seconds) && typeof data.duration !== "object") {
		durationStr =
			seconds < 60
				? `${seconds}s`
				: `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, "0")}`;
	} else {
		durationStr = data.duration || "0s";
	}

	return {
		id,
		createdAt: createdAtDate.toISOString(),
		caller: {
			name: aiCallerName || "Unknown",
			phone: data.from || "",
			email: data.callerEmail,
		},
		agent: data.agentName || "agent1",
		inboundNumber: data.inboundNumber || data.to || "",
		occurredAt: occurredAtDate,
		date,
		time,
		duration: durationStr,
		status: isCallStatus(data.status) ? data.status : CALL_STATUS.COMPLETED,
		transcriptionWarning:
			(data.transcriptionWarning?.code === "single-transcript-warning" ||
				data.transcriptionWarning?.code === "transcript-unavailable") &&
			Array.isArray(data.transcriptionWarning.failedRecognizerIds) &&
			Array.isArray(data.transcriptionWarning.successfulRecognizerIds)
				? data.transcriptionWarning
				: null,
		summary: data.summary ?? "",
		transcript:
			typeof data.formattedTranscript === "string"
				? data.formattedTranscript
				: "",
		collectedNumbers: Array.isArray(data.collectedNumbers)
			? data.collectedNumbers
			: [],
		isTestCall: data.isTestCall || false,
		title: data.title ?? "",
		gcsUri: data.gcsUri,
		companyId,
		actionItems: [], // populated by data context
		evaluationScore: data.evaluationScore,
		evaluationRubric:
			data.evaluationRubric === "inbound_receptionist" ||
			data.evaluationRubric === "outbound_playbook"
				? data.evaluationRubric
				: null,
		evaluationStatus: data.evaluationStatus,
		isProblematic: data.isProblematic,
		latencyMs,
		evaluationUsage: data.evaluationUsage,
		evaluationCost: data.evaluationCost,
		source: typeof data.source === "string" ? data.source : null,
		from: typeof data.from === "string" ? data.from : null,
		outboundPlaybookRunId:
			typeof data.outboundPlaybookRunId === "string"
				? data.outboundPlaybookRunId
				: null,
		outboundTaskId:
			typeof data.outboundTaskId === "string" ? data.outboundTaskId : null,
		outboundTaskAttemptId:
			typeof data.outboundTaskAttemptId === "string"
				? data.outboundTaskAttemptId
				: null,
		outboundPlaybookId:
			typeof data.outboundPlaybookId === "string"
				? data.outboundPlaybookId
				: null,
		outboundPlaybookVersion:
			typeof data.outboundPlaybookVersion === "number"
				? data.outboundPlaybookVersion
				: null,
		destinationNumber:
			typeof data.destinationNumber === "string"
				? data.destinationNumber
				: null,
		operatorPhoneNumber:
			typeof data.operatorPhoneNumber === "string"
				? data.operatorPhoneNumber
				: null,
		operatorName:
			typeof data.operatorName === "string" ? data.operatorName : null,
		callType:
			data.callType === "human" || data.callType === "ai"
				? data.callType
				: null,
		direction:
			data.direction === "inbound" || data.direction === "outbound"
				? data.direction
				: null,
		isOutbound: data.isOutbound === true,
		isPiiRedacted: data.isPiiRedacted ?? false,
		piiProcessing: data.piiProcessing ?? null,
		piiRedactedAt: data.piiRedactedAt?.toDate
			? data.piiRedactedAt.toDate()
			: data.piiRedactedAt || null,
		isTwilioRecordingPurged: data.isTwilioRecordingPurged ?? false,
		twilioRecordingPurgedAt: data.twilioRecordingPurgedAt?.toDate
			? data.twilioRecordingPurgedAt.toDate()
			: data.twilioRecordingPurgedAt || null,
		isPiiSidecarPurged: data.isPiiSidecarPurged ?? false,
		piiSidecarPurgeOutcome:
			data.piiSidecarPurgeOutcome === "deleted" ||
			data.piiSidecarPurgeOutcome === "already_absent"
				? data.piiSidecarPurgeOutcome
				: null,
		piiSidecarPurgedAt: data.piiSidecarPurgedAt?.toDate
			? data.piiSidecarPurgedAt.toDate()
			: data.piiSidecarPurgedAt || null,
	};
}
